import { LogService } from 'library/ts/utilities/logger/LogService';

/**
 * @description Debounce function from underscore library.
 * Returns a function, that, as long as it continues to be invoked, will not
 * be triggered. The function will be called after it stops being called for
 * N milliseconds. If `immediate` is passed, trigger the function on the
 * leading edge, instead of the trailing.
 * @see {@link https://davidwalsh.name/javascript-debounce-function}
 * @param {() => void} func
 * @param {number} wait
 * @param {boolean} immediate
 * @return {() => void}
 * @example
 * function myEfficientFn() {
 *     // All the taxing stuff you do
 * }
 * window.addEventListener( 'resize', debounce( myEfficientFn, 250, true ) );
 */
export const debounce = ( func: () => void, wait: number, immediate = false ): () => void => {
	let timeout;
	return function ( ...args ) {
		const later = () => {
			timeout = null; // added this to set same behaviour as ES5
			if ( !immediate ) func.apply( this, args ); // this is called conditionally, just like in the ES5 version
		};
		const callNow = immediate && !timeout;
		clearTimeout( timeout );
		timeout = setTimeout( later, wait );
		if ( callNow ) func.apply( this, args );
	};
};

/**
 * @description Utility class for checking is we can execute or throttle a function
 * @example
 * const throttler = new Throttler(200);  // 200ms throttle interval
 *
 * function doSomething() {
 *   if (throttler.canExecute()) {
 *     console.log('Executing action');
 *   } else {
 *     console.log('Throttled, waiting...');
 *   }
 * }
 *
 * // Example usage
 * setInterval(doSomething, 50);  // Tries to execute every 50ms
 */
export class Throttler {
	#lastExecutionTime: number = 0;
	#throttleInterval: number;

	constructor( interval: number = 200 ) {
		this.#throttleInterval = interval;
	}

	canExecute = (): boolean => {
		const currentTime = Date.now();
		const timeSinceLastExecution = currentTime - this.#lastExecutionTime;

		if ( timeSinceLastExecution >= this.#throttleInterval ) {
			this.#lastExecutionTime = currentTime;
			return true;
		}

		LogService.create( { prefixes: [ 'SmartTag:', 'throttler' ] } ).warn(
			'execution prevented because ',
			'timeSinceLastExecution (',
			timeSinceLastExecution,
			')',
			'is less than ',
			'this.#throttleInterval (',
			this.#throttleInterval,
			')'
		);
		return false;
	};
}

/**
 * @description Limits how frequent a function can be executed.
 * With the option to execute any pending calls at the end of the timeout.
 * @example
 * const precisionThrottler = new PrecisionThrottler( 200, true );  // 200ms: throttle interval | true: consider pending calls
 *
 * // Example usage
 * setInterval(
 * 		// Throttler
 * 		precisionThrottler.execute(
 * 			(): void => {
 * 				// run your function with your arguments
 * 				doSomething( x, y, z );
 * 			}
 * 		), 50);  // Tries to execute every 50ms
 */
export class PrecisionThrottler {
	/**
	 * @description Flag to consider pending calls and execute at the end.
	 * @type {boolean}
	 */
	readonly #applyPendingCall: boolean;
	/**
	 * @description Given throttling time in milliseconds.
	 * @type {number}
	 */
	#throttleTime: number;
	/**
	 * @description Pending call Flag.
	 * @type {boolean}
	 */
	#isCallPending: boolean = false;
	/**
	 * @description Throttling Flag.
	 * @type {boolean}
	 */
	#isThrottled: boolean = false;
	/**
	 * @description Throttling timer.
	 * @type {NodeJS.Timeout | number | null}
	 */
	#timer: NodeJS.Timeout | number | null = null;

	constructor( interval: number = 0, applyPending: boolean = false ) {
		this.#throttleTime = interval;
		this.#applyPendingCall = applyPending || false;
	}

	/**
	 * @description Get Throttling Flag
	 * @returns {boolean}
	 */
	get isThrottled(): boolean {
		return this.#isThrottled;
	}

	/**
	 * @description Pending call Flag
	 * @returns {boolean}
	 */
	get isCallPending(): boolean {
		return this.#isCallPending;
	}

	/**
	 * @description Get throttling time
	 * @returns {number}
	 */
	get throttleTime(): number {
		return this.#throttleTime;
	}

	/**
	 * @description Set throttling time
	 * @param {number} time throttle time
	 */
	set throttleTime( time: number ) {
		this.#throttleTime = time ?? 0;
	}

	/**
	 * @description Execute throttled callback
	 * @param {() => void} func callback
	 */
	execute( func: () => void ): void {
		// Check if we are throttled, save pending call and return
		if ( this.#isThrottled ) {
			// Set pending call's flag
			this.#isCallPending = true;
			return;
		}
		// Set throttling flag
		this.#isThrottled = true;
		// fire callback
		func?.call( this );
		// Clear timer
		clearTimeout( this.#timer );
		// Set throttling timer
		this.#timer = setTimeout(
			(): void => {
				// Reset throttling flag
				this.#isThrottled = false;
				// Check if there is a pending call
				if ( this.#applyPendingCall && this.#isCallPending ) {
					// fire callback
					func?.call( this );
					// Reset pending call's flag
					this.#isCallPending = false;
				}
			}, this.#throttleTime );
	}

	/**
	 * @description Clear throttler
	 */
	clear(): void {
		// Clear timer
		clearTimeout( this.#timer );
		// Reset pending call's flag
		this.#isCallPending = false;
		// Reset throttling flag
		this.#isThrottled = false;
	}
}

/**
 * @description Executes array of promises in sequential fashion
 * @param {{(...args): Promise<unknown>}[]} tasks
 * @returns {Promise<unknown[]>}
 * @example
 * runPromiseArray(
 *  [
 *     () => { return new Promise( ( resolve, reject ) => { resolve( 'Function 1 => Resolved' ); } ) },
 *     () => { return new Promise( ( resolve, reject ) => { resolve( 'Function 2 => Resolved' ); } ) },
 *     () => { return new Promise( ( resolve, reject ) => { resolve( 'Function 3 => Resolved' ); } ) }
 *  ]
 * ).then(
 *     ( output ) => {
 *          console.log( 'All done:', 'Results:', output );
 *     }
 * );
 */
export const runPromiseArray = ( tasks: { ( ...args: any ): Promise<unknown> }[] ): Promise<unknown[]> => {
	const results: unknown[] = [];
	const logger = LogService.create( { prefixes: [ 'SmartTag:', 'Utilities.Function.runPromiseArray' ] } );

	return new Promise(
		( resolve, reject ) => {

			const execute = ( tasks ) => {
				logger.log( 'execute:', 'tasks:', tasks );

				if ( tasks && tasks.length > 0 ) {
					const task = tasks.shift();

					return task()
						.then(
							( output ) => {
								logger.log( 'execute:', 'task:', output );
								results.push( output );
								execute( tasks );
							}
						)
						.catch(
							( error ) => {
								logger.warn( error );
								results.push( error );
								execute( tasks );
							}
						);
				}

				logger.log( 'execute:', 'no tasks left...' );
				resolve( results );
			};

			execute( tasks );
		}
	);
};